import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Unlock, Loader2, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getForceCloseDocuments, forceCloseDocument, reopenDocument } from '@/services/fobSectionApi';

const FOBForceClose = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [closeTarget, setCloseTarget] = useState(null);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getForceCloseDocuments();
      setDocs(data.documents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doForceClose = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      const res = await forceCloseDocument(closeTarget.entry_id, reason.trim());
      toast.success(`Force-closed — shortage ${res.shortage_qty}, excess ${res.excess_qty}`);
      setCloseTarget(null);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to force-close');
    } finally {
      setBusy(false);
    }
  };

  const doReopen = async () => {
    if (!reopenTarget) return;
    setBusy(true);
    try {
      await reopenDocument(reopenTarget.entry_id);
      toast.success('Document re-opened');
      setReopenTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-open');
    } finally {
      setBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? docs.filter((d) =>
        (d.document_barcode || '').toLowerCase().includes(q) ||
        (d.vendor_name || '').toLowerCase().includes(q) ||
        (d.entry_id || '').toLowerCase().includes(q))
    : docs;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sections/fob')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Lock className="w-6 h-6 text-amber-400" />
          <div>
            <h1 className="text-xl font-bold font-display">Force-Close Documents</h1>
            <p className="text-sm text-[#E8DCC8]">FOB — close short/excess documents, re-open later (admin)</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by document / vendor / entry id"
            className="max-w-md"
            data-testid="fc-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-[#5A5F6E]" data-testid="fc-empty">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              No pre-pasted documents found.
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid="fc-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EC] text-[#5A5F6E]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Document</th>
                  <th className="text-left px-3 py-2.5 font-medium">Vendor</th>
                  <th className="text-right px-3 py-2.5 font-medium">Entered</th>
                  <th className="text-right px-3 py-2.5 font-medium">Scanned</th>
                  <th className="text-right px-3 py-2.5 font-medium">Short / Excess</th>
                  <th className="text-left px-3 py-2.5 font-medium">Status</th>
                  <th className="text-right px-3 py-2.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.entry_id} className="border-t border-[#E5DFD0] hover:bg-[#FBFAF5]" data-testid={`fc-row-${d.entry_id}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1A1F2E]">{d.document_barcode}</td>
                    <td className="px-3 py-2.5">{d.vendor_name || '—'}</td>
                    <td className="px-3 py-2.5 text-right">{d.entered_qty}</td>
                    <td className="px-3 py-2.5 text-right">{d.scanned_qty}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-red-600">{d.shortage_qty}</span>
                      {' / '}
                      <span className="text-amber-700">{d.excess_qty}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {d.force_closed
                        ? <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded" title={d.force_close_reason || ''}>Force-closed</span>
                        : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Open</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {d.force_closed ? (
                        <Button variant="outline" size="sm" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" onClick={() => setReopenTarget(d)} data-testid={`fc-reopen-${d.entry_id}`}>
                          <Unlock className="w-4 h-4 mr-1" />Re-open
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => { setCloseTarget(d); setReason(''); }} data-testid={`fc-close-${d.entry_id}`}>
                          <Lock className="w-4 h-4 mr-1" />Force-close
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Force-close dialog */}
      <Dialog open={!!closeTarget} onOpenChange={(o) => { if (!o) setCloseTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="fc-close-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Force-close {closeTarget?.document_barcode}
            </DialogTitle>
            <DialogDescription>
              This logs a shortage of <strong>{closeTarget?.shortage_qty}</strong> and excess of{' '}
              <strong>{closeTarget?.excess_qty}</strong>, and removes the document from the operator list.
              You can re-open it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. balance pieces not received — closing" data-testid="fc-close-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)} data-testid="fc-close-cancel">Cancel</Button>
            <Button onClick={doForceClose} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fc-close-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Force-close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-open dialog */}
      <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) setReopenTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="fc-reopen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-emerald-600" />
              Re-open {reopenTarget?.document_barcode}
            </DialogTitle>
            <DialogDescription>
              This re-opens the document so operators can scan it again in Assortment Packing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)} data-testid="fc-reopen-cancel">Cancel</Button>
            <Button onClick={doReopen} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fc-reopen-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Re-open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FOBForceClose;
